<template>
  <div v-if="loadingActiveBoard">
    <el-skeleton class="m-2 ml-4 mt-4 mr-5" :rows="10" animated />
  </div>
  <div v-else>
    <div class="card-container mt-4" v-if="activeBoard && activeBoard.id">
      <el-row :gutter="10" class="board">
        <el-col :span="6" v-for="(bugs, status) in bugStatuses" :key="status">
          <div
            class="board-column"
            @dragenter.prevent
            @dragover.prevent
            @drop="handleDrop($event, status)"
          >
            <div class="board-header">
              {{ getStatus(status) }} {{ bugs.length ? bugs.length : '' }}
            </div>
            <div class="content">
              <transition-group name="bug-list">
                <div
                  v-for="(bugReport, index) in bugs"
                  :key="bugReport.id"
                  draggable="true"
                  @dragover.prevent="handleDragover(bugReport, index)"
                  @dragstart="startDrag($event, bugReport)"
                >
                  <bug-summary-container-vue :bug-report="bugReport" @open-dialog="openDialog" />
                </div>
              </transition-group>
            </div>
          </div>
        </el-col>
      </el-row>
      <el-dialog
        :model-value="dialogVisible"
        width="70%"
        @close="onDialogClose"
      >
        <BugReportDetails  :id="selectedBugReportId" @reload-reports="needReloadAfterDialogClose = true"></BugReportDetails>
      </el-dialog>
    </div>
    <div v-else>
      <div class="text-center mt-5">
        <el-empty description="No active board found. Create a board in Backlog."></el-empty>
      </div>
    </div>
  </div>
  </template>

<script>
import { xray } from '../../../config/pluginInit'
import { onMounted, ref } from 'vue'
import getBugReports from '../../../composables/getBugReports'
import BugSummaryContainerVue from './BugSummaryContainer.vue'
import { updateDocument } from '../../../services/useDocument'
import BugReportDetails from './BugReportDetails.vue'

export default {
  name: 'BugSubmissions',
  components: {
    BugReportDetails,
    BugSummaryContainerVue
  },
  setup () {
    const { loadBugReportsBasedOnStatus, getActiveBoard } = getBugReports()
    const todo = ref([])
    const inProgress = ref([])
    const inReview = ref([])
    const done = ref([])
    const dragOverId = ref(null)
    const dragOverPriority = ref(null)
    const dragOverIndex = ref(null)
    const selectedBugReportId = ref(null)
    const dialogVisible = ref(false)
    const needReloadAfterDialogClose = ref(false)
    const activeBoard = ref(null)
    const loadingActiveBoard = ref(true)

    const bugStatuses = ref({
      todo,
      inProgress,
      inReview,
      done
    })
    const draggedItem = ref(null)

    const getStatus = (status) => {
      switch (status) {
        case 'todo':
          return 'To Do'
        case 'inProgress':
          return 'In Progress'
        case 'inReview':
          return 'In Review'
        case 'done':
          return 'Done'
        default:
          return 'To Do'
      }
    }

    const getStatusReverse = (status) => {
      switch (status) {
        case 'To Do':
          return 'todo'
        case 'In Progress':
          return 'inProgress'
        case 'In Review':
          return 'inReview'
        case 'Done':
          return 'done'
        default:
          return 'todo'
      }
    }

    onMounted(async () => {
      loadingActiveBoard.value = true
      activeBoard.value = await getActiveBoard()
      loadingActiveBoard.value = false
      if (activeBoard.value && activeBoard.value.id) {
        const bugs = await loadBugReportsBasedOnStatus(activeBoard.value.id)
        todo.value = bugs['To Do']
        inProgress.value = bugs['In Progress']
        inReview.value = bugs['In Review']
        done.value = bugs.Done
      }
    })

    const startDrag = (evt, bugReport) => {
      draggedItem.value = bugReport
      evt.dataTransfer.effectAllowed = 'move'
      evt.dataTransfer.dropEffect = 'move'
    }

    const allowDrop = (event) => {
      event.preventDefault()
    }

    const handleDragover = (bugReport, index) => {
      dragOverId.value = bugReport.id
      dragOverPriority.value = bugReport.priority
      dragOverIndex.value = index
    }

    const handleDrop = (event, newStatus) => {
      if (draggedItem.value) {
        const oldStatus = getStatusReverse(draggedItem.value.status || 'To Do')

        const oldIndex = bugStatuses.value[oldStatus].findIndex(bug => bug.id === draggedItem.value.id)

        if (oldIndex !== -1) {
          if (newStatus === oldStatus) {
            handleSameStatusDrop(newStatus, oldIndex)
          } else {
            handleDifferentStatusDrop(newStatus, oldStatus, oldIndex)
          }
        }
        // Clear the dragged item and dragOverId after dropping
        draggedItem.value = null
        dragOverId.value = null
        dragOverPriority.value = null
        dragOverIndex.value = null
      }
    }

    const handleSameStatusDrop = (status, oldIndex) => {
      const newIndex = bugStatuses.value[status].findIndex(bug => bug.id === dragOverId.value)
      const [item] = bugStatuses.value[status].splice(oldIndex, 1)

      // If dragOverId is not found, append to the end. Otherwise, insert at the specific index
      if (newIndex === -1) {
        console.log('Error: dragOverId not found in the list')
      }

      bugStatuses.value[status].splice(newIndex, 0, item)
      bugStatuses.value[status].forEach((bug, index) => {
        bug.priority = index
        updateReportPriority(bug.id, index)
      })
    }

    const handleDifferentStatusDrop = (newStatus, oldStatus, oldIndex) => {
      const { handlelUpdate } = updateDocument('bugReports', draggedItem.value.id)

      // Dragged item is dropped in the middle of the new list
      if (draggedItem.value.id !== dragOverId.value) {
        const newIndex = bugStatuses.value[newStatus].findIndex(bug => bug.id === dragOverId.value)
        const [item] = bugStatuses.value[oldStatus].splice(oldIndex, 1)
        bugStatuses.value[newStatus].splice(newIndex, 0, item)
      } else {
        // Dragged item is dropped to the end of the new list
        const [item] = bugStatuses.value[oldStatus].splice(oldIndex, 1)
        bugStatuses.value[newStatus].push(item)
      }
      draggedItem.value.status = getStatus(newStatus)
      handlelUpdate({ status: getStatus(newStatus) })
      bugStatuses.value[newStatus].forEach((bug, index) => {
        bug.priority = index
        updateReportPriority(bug.id, index)
      })
    }

    const updateReportPriority = async (id, priority) => {
      const { handlelUpdate } = updateDocument('bugReports', id)
      handlelUpdate({ priority })
    }

    const openDialog = (id) => {
      dialogVisible.value = true
      selectedBugReportId.value = id
      needReloadAfterDialogClose.value = false
    }

    const onDialogClose = async () => {
      dialogVisible.value = false
      if (needReloadAfterDialogClose.value && activeBoard.value && activeBoard.value.id) {
        const bugs = await loadBugReportsBasedOnStatus(activeBoard.value.id)
        todo.value = bugs['To Do']
        inProgress.value = bugs['In Progress']
        inReview.value = bugs['In Review']
        done.value = bugs.Done
      }
      needReloadAfterDialogClose.value = false
    }

    return {
      todo,
      inProgress,
      inReview,
      done,
      startDrag,
      bugStatuses,
      allowDrop,
      handleDrop,
      handleDragover,
      selectedBugReportId,
      dialogVisible,
      openDialog,
      getStatus,
      needReloadAfterDialogClose,
      onDialogClose,
      activeBoard,
      loadingActiveBoard
    }
  },
  mounted () {
    xray.index()
  }
}
</script>

  <style scoped>
  .board-column {
    background-color: #f4f5f7;
    border-radius: 6px;
    min-height: 84px;
    height: 100%;
  }

  .card-container {
    margin: 20px 20px 20px 20px;
  }

  .card-header {
    background: transparent;
    border-bottom: none;
  }

  .card-header span {
    font-size: 16px;
    font-weight: 500;
    color: var(--text-color) !important;
  }

  .board-header {
    padding: 20px 10px;
    font-size: 12px;
    color: #5e6c84;
    text-transform: uppercase;
    font-weight: 500;
    border-radius: 6px 6px 0 0;
    letter-spacing: normal;
    line-height: 1.14286;
  }

  .board {
    align-items: stretch;
  }

  .content {
    padding: 0 5px;
    padding-bottom: 90px;
  }

  .bug-list-enter-active,
  .bug-list-leave-active {
    transition: all 0.5s ease;
  }
  .bug-list-enter-from,
  .bug-list-leave-to {
    opacity: 0;
    transform: translateY(30px);
  }
  .bug-list-enter-to,
  .bug-list-leave-from {
    opacity: 1;
    transform: translateY(0);
  }

  </style>
